<template>
  <div class="bg-white rounded-lg p-4" :data-testid="dataTestId">
    <div class="overflow-auto">
      <table
        class="min-w-full bg-white rounded-md table-fixed"
        :data-testid="`${dataTestId}-table`"
      >
        <thead>
          <tr
            class="bg-[#F9FAFB] border-b border-[#E4E7EC]"
            :data-testid="`${dataTestId}-header-row`"
          >
            <th
              v-for="(column, index) in columns"
              :key="index"
              :class="[
                'py-2 px-2 font-semibold text-university-primary text-left',
                column.headerClass,
              ]"
              :data-testid="`${dataTestId}-header-${column.key}`"
              :style="column.style"
            >
              <span v-if="column.headerRender">
                <span v-html="column.headerRender(column)"></span>
              </span>
              <span v-else>{{ column.label }}</span>
            </th>
          </tr>
        </thead>
        <tbody :data-testid="`${dataTestId}-body`">
          <tr
            v-for="(row, rowIndex) in limitedRows"
            :key="rowIndex"
            class="border-b border-[#E4E7EC]"
            :data-testid="`${dataTestId}-row-${rowIndex}`"
          >
            <td
              v-for="(column, colIndex) in columns"
              :key="colIndex"
              :class="[
                'py-2 px-2 text-university-primary text-left',
                column.bodyClass,
              ]"
              :data-testid="`${dataTestId}-cell-${rowIndex}-${column.key}`"
              :style="column.style"
            >
              <component
                :is="row[column.key].component"
                v-if="isComponent(row[column.key])"
                v-bind="row[column.key].props"
                v-on="row[column.key].on || {}"
              />
              <span
                v-else-if="column.render"
                v-html="column.render(row[column.key], row)"
              ></span>
              <span v-else v-html="renderSafeHtml(row, column)"></span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import DOMPurify from "dompurify";
import { computed } from "vue";

DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

const props = defineProps({
  columns: {
    type: Array,
    required: true,
    default: () => [],
  },
  rows: {
    type: Array,
    required: true,
    default: () => [],
  },
  dataTestId: {
    type: String,
    required: true,
  },
  rowLimit: {
    type: Number,
    required: false,
  },
});

const renderSafeHtml = (row, column) => {
  if (column.render) {
    const rawHTML = column.render(row[column.key], row);
    return DOMPurify.sanitize(rawHTML);
  }
  return DOMPurify.sanitize(row[column.key] ?? "-");
};

const isComponent = (value) => {
  return value && typeof value === "object" && value.component;
};

const limitedRows = computed(() => props.rows.slice(0, props.rowLimit));
</script>
